/**
 * Pure date derivation for the calendar dashboard: the month grid of the calendar widget,
 * per-day event counts, day grouping of the meetings list and the "next upcoming" pick.
 * Everything here is deterministic on its inputs (a `now` is always passed in) so the suites
 * never depend on the wall clock.
 */

import type { CalendarEvent } from './api';

/** Number of milliseconds in one day, for range arithmetic. */
export const DAY_MS = 24 * 60 * 60 * 1000;

/** ISO weekday number (1=Monday .. 7=Sunday) of a Date, in the runtime's local time. */
export function isoWeekday(date: Date): number {
  const sundayFirst = date.getDay();
  return sundayFirst === 0 ? 7 : sundayFirst;
}

/** Local-midnight Date of the day containing `date`. */
export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * The six-week (42-cell) Monday-first grid covering `year`/`monthIndex` (0-based). Leading and
 * trailing cells borrow days from the adjacent months, exactly as the month widget renders them.
 */
export function monthGrid(year: number, monthIndex: number): Date[] {
  const first = new Date(year, monthIndex, 1);
  const gridStart = new Date(year, monthIndex, 1 - (isoWeekday(first) - 1));
  return Array.from({ length: 42 }, (_, index) => {
    const cell = new Date(gridStart);
    cell.setDate(gridStart.getDate() + index);
    return cell;
  });
}

/** yyyy-mm-dd of a local date, the key the per-day maps use. */
export function dayKey(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** True when both dates fall on the same local calendar day. */
export function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * The instant that placed the entry in the listing: the occurrence start when the entry is an
 * expanded RRULE occurrence, the base start otherwise. Every display path renders this, not
 * the raw startsAt (which stays the series' base fact).
 */
export function eventStart(event: CalendarEvent): Date {
  return new Date(event.occurrenceStartsAt ?? event.startsAt);
}

/** Occurrence end when the entry is an occurrence, the base end otherwise. */
export function eventEnd(event: CalendarEvent): Date {
  return new Date(event.occurrenceEndsAt ?? event.endsAt);
}

/**
 * Stable list/React key: one recurring series returns one entry per occurrence, all sharing
 * the id, so the key pins the occurrence start ("the client dedups by id+occurrenceStart").
 */
export function eventKey(event: CalendarEvent): string {
  return event.occurrenceStartsAt === null
    ? `${event.id}`
    : `${event.id}:${event.occurrenceStartsAt}`;
}

/** True when the event is running at `now` (inclusive bounds, so a just-started event counts). */
export function isEventLive(event: CalendarEvent, now: Date): boolean {
  const start = eventStart(event).getTime();
  const end = eventEnd(event).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) {
    return false;
  }
  const at = now.getTime();
  return start <= at && at <= end;
}

/**
 * Counts events per local day within one month (0-based monthIndex), occurrences included:
 * every listing entry counts for the day its occurrenceStartsAt (or startsAt) falls on. Days
 * outside the month are never present; the widget only asks for the visible month.
 */
export function eventCountByDay(
  events: readonly CalendarEvent[],
  year: number,
  monthIndex: number,
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const event of events) {
    const start = eventStart(event);
    if (Number.isNaN(start.getTime())) {
      continue;
    }
    if (start.getFullYear() !== year || start.getMonth() !== monthIndex) {
      continue;
    }
    const key = dayKey(start);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

/** Groups events by their local day, preserving the caller's order inside each group. */
export function groupEventsByDay(
  events: readonly CalendarEvent[],
): Array<{ readonly day: Date; readonly events: CalendarEvent[] }> {
  const groups = new Map<string, { day: Date; events: CalendarEvent[] }>();
  for (const event of events) {
    const start = eventStart(event);
    if (Number.isNaN(start.getTime())) {
      continue;
    }
    const key = dayKey(start);
    const group = groups.get(key);
    if (group === undefined) {
      groups.set(key, { day: startOfDay(start), events: [event] });
    } else {
      group.events.push(event);
    }
  }
  return [...groups.values()];
}

/**
 * The next upcoming event: the one with the earliest occurrence start among events whose
 * occurrence end has not passed at `now`. Null when every event is over (or the list is empty).
 */
export function nextUpcoming(events: readonly CalendarEvent[], now: Date): CalendarEvent | null {
  const at = now.getTime();
  let best: CalendarEvent | null = null;
  let bestStart = Number.POSITIVE_INFINITY;
  for (const event of events) {
    const start = eventStart(event).getTime();
    const end = eventEnd(event).getTime();
    if (Number.isNaN(start) || Number.isNaN(end) || end < at) {
      continue;
    }
    if (start < bestStart) {
      best = event;
      bestStart = start;
    }
  }
  return best;
}

/** HH:MM of a local date, 24-hour. */
export function formatTime(date: Date): string {
  const hours = `${date.getHours()}`.padStart(2, '0');
  const minutes = `${date.getMinutes()}`.padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * The events from `all` whose window intersects [start, end) - the dialog's conflict list.
 * Windows are half-open, so back-to-back rows (10:00-11:00 vs 11:00-12:00) do NOT conflict;
 * occurrence windows are compared (eventStart/eventEnd), and rows sharing `excludeId` (the
 * edited series' base id) are skipped - a series never conflicts with itself. Malformed rows
 * (unparsable windows) are ignored rather than thrown.
 */
export function findOverlappingEvents(
  all: readonly CalendarEvent[],
  start: Date,
  end: Date,
  excludeId?: number,
): CalendarEvent[] {
  const from = start.getTime();
  const to = end.getTime();
  if (Number.isNaN(from) || Number.isNaN(to) || to <= from) {
    return [];
  }
  return all.filter((event) => {
    if (excludeId !== undefined && event.id === excludeId) {
      return false;
    }
    const otherStart = eventStart(event).getTime();
    const otherEnd = eventEnd(event).getTime();
    return (
      !Number.isNaN(otherStart) && !Number.isNaN(otherEnd) && otherStart < to && otherEnd > from
    );
  });
}
